import { Connection } from "@solana/web3.js";
import type { Cluster } from "@oxminter/shared/cluster";

export interface NetworkChoice {
  key: string;
  label: string;
  cluster: Cluster;
  rpcUrl: string;
  /** Wallet Standard chain identifier used when requesting a signature. */
  chain: string;
  danger: boolean;
}

/**
 * "localnet" is not a separate cluster here: solana-test-validator runs the
 * programs cloned from devnet, so the devnet program IDs are the correct ones.
 * Only the RPC endpoint differs. Getting this wrong points the app at the
 * mainnet CPMM program on a chain where it does not exist.
 */
export const NETWORKS: NetworkChoice[] = [
  {
    key: "localnet",
    label: "Localnet (test validator)",
    cluster: "devnet",
    rpcUrl: "http://127.0.0.1:8899",
    chain: "solana:localnet",
    danger: false,
  },
  {
    key: "devnet",
    label: "Devnet",
    cluster: "devnet",
    rpcUrl: "https://api.devnet.solana.com",
    chain: "solana:devnet",
    danger: false,
  },
  {
    key: "mainnet",
    label: "Mainnet-beta — REAL FUNDS",
    cluster: "mainnet-beta",
    rpcUrl: "",
    chain: "solana:mainnet",
    danger: true,
  },
];

export function makeConnection(rpcUrl: string): Connection {
  return new Connection(rpcUrl, {
    commitment: "confirmed",
    confirmTransactionInitialTimeout: 120_000,
  });
}

export function explorerUrl(net: NetworkChoice, kind: "tx" | "address", id: string): string {
  if (net.key === "localnet") {
    return `https://explorer.solana.com/${kind}/${id}?cluster=custom&customUrl=${encodeURIComponent(
      net.rpcUrl
    )}`;
  }
  const suffix = net.cluster === "devnet" ? "?cluster=devnet" : "";
  return `https://explorer.solana.com/${kind}/${id}${suffix}`;
}

/**
 * A page served over https cannot fetch an http:// endpoint: the browser
 * blocks it as mixed content before the request leaves, and the only thing the
 * application sees is a bare `TypeError: Failed to fetch`.
 *
 * This is not hypothetical. The console defaults to Localnet, whose RPC is
 * http://127.0.0.1:8899, so the FIRST thing a freshly deployed console at
 * https://app.oxminter.com did was fail to read the fee tiers and report
 * "Failed to fetch" — a message that names neither the cause nor the fix.
 */
export function isMixedContentRpc(url: string, pageProtocol: string): boolean {
  return pageProtocol === "https:" && /^http:\/\//i.test(url);
}

/**
 * Which network to start on. Localnet is only reachable from a page that is
 * itself served over http, so a deployed console must not default to it.
 */
export function defaultNetworkKey(pageProtocol: string): string {
  return pageProtocol === "https:" ? "devnet" : "localnet";
}

export function validateRpcUrl(url: string, cluster: Cluster, pageProtocol = "http:"): string | null {
  const loopback = /^http:\/\/(127\.0\.0\.1|localhost|\[::1\])(:\d+)?(\/|$)/.test(url);
  if (isMixedContentRpc(url, pageProtocol)) {
    return "this page is served over https, so the browser blocks requests to an http:// endpoint — pick Devnet, or run the console locally over http to use a test validator";
  }
  if (!/^https:\/\//.test(url) && !loopback) {
    return "RPC URL must be https://, or http:// to 127.0.0.1 for a local validator";
  }
  if (cluster === "mainnet-beta" && loopback) {
    return "mainnet-beta cannot use a loopback RPC URL";
  }
  if (cluster === "mainnet-beta" && /api\.mainnet-beta\.solana\.com/.test(url)) {
    return "the public mainnet endpoint is rate-limited and drops transactions under load; use a paid RPC provider";
  }
  return null;
}
